<template>
  <div class="order-header mb-4">
    <!-- Status badges and actions -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center flex-wrap">
        <CBadge :color="statusColor" class="mr-2 badge-outline">
          {{ order.status }}
        </CBadge>
        <CBadge v-if="order.payment_platform" color="info" class="mr-2 badge-outline">
          {{ order.payment_platform }}
        </CBadge>
        <CBadge v-if="order.payment_method" color="light" class="payment-badge">
          <CIcon :name="paymentMethodIcon" size="sm" class="mr-1" v-if="paymentMethodIcon"/>
          {{ order.payment_method.label }}
        </CBadge>
      </div>

      <CButton
        color="primary"
        size="sm"
        @click="goToOrderDetail"
      >
        <translate translate-context="accounting.order">View in Orders</translate>
      </CButton>
    </div>

    <!-- Info cards row -->
    <CRow>
      <!-- Customer & Payment Info -->
      <CCol lg="6">
        <CCard class="h-100">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipShoppingBag01" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="accounting.refund">Order details</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol>
                <div class="order-row">
                  <div class="label">
                    <translate translate-context="accounting.order">Customer</translate>
                  </div>
                  <div>
                    {{ order.user_email || '-' }}
                  </div>
                </div>
                <div class="order-row">
                  <div class="label">
                    <translate translate-context="accounting.order">Billing Country</translate>
                  </div>
                  <div>
                    {{ order.billing_country || '-' }}
                  </div>
                </div>
                <div class="order-row">
                  <div class="label">
                    <translate translate-context="accounting.order">Created</translate>
                  </div>
                  <div>
                    {{ order.created_at|formatDateTime('L LT') }}
                  </div>
                </div>
                <div class="order-row">
                  <div class="label">
                    <translate translate-context="accounting.order">Paid</translate>
                  </div>
                  <div>
                    {{ order.paid_at|formatDateTime('L LT') }}
                  </div>
                </div>
                <div class="order-row" v-if="order.payment_method">
                  <div class="label">
                    <translate translate-context="accounting.order">Payment Method</translate>
                  </div>
                  <div>
                    {{ order.payment_method.label }}
                  </div>
                </div>
                <div class="order-row" v-if="order.payment_transaction_id">
                  <div class="label">
                    <translate translate-context="accounting.order">Transaction ID</translate>
                  </div>
                  <div>
                    <CBadge class="badge-outline mr-1" color="light">
                      {{ order.payment_transaction_id }}
                    </CBadge>
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Financial Breakdown -->
      <CCol lg="6">
        <CCard class="h-100">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipCreditCard02" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="accounting.refund">Payment details</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol>
                <!-- Context: how the Grand Total is composed -->
                <div class="order-row">
                  <div class="label">
                    <translate translate-context="accounting.order">Items Subtotal</translate>
                    <span class="ml-2">
                      ({{
                        order.price_digital_items_total|currency(order.currency)
                      }} + {{ order.price_print_items_total|currency(order.currency) }})
                    </span>
                  </div>
                  <div>
                    {{ itemsSubtotal|currency(order.currency) }}
                  </div>
                </div>
                <div class="order-row" v-if="hasHandlingFee">
                  <div class="label">
                    <translate translate-context="accounting.order">Handling Fee</translate>
                  </div>
                  <div>
                    {{ order.handling_fee|currency(order.currency) }}
                  </div>
                </div>
                <div class="order-row" v-if="hasShipping">
                  <div class="label">
                    <translate translate-context="accounting.order">Shipping</translate>
                  </div>
                  <div>
                    {{ order.price_shipping|currency(order.currency) }}
                  </div>
                </div>

                <!-- Grand Total: starting point -->
                <div class="order-row order-row--border">
                  <div class="label font-weight-semi-bold">
                    <translate translate-context="accounting.order">Grand Total</translate>
                  </div>
                  <div class="font-weight-semi-bold">
                    {{ order.price_grandtotal|currency(order.currency) }}
                  </div>
                </div>

                <!-- Deductions -->
                <div class="order-row" v-if="hasHandlingFee">
                  <div class="label pl-3">
                    <translate translate-context="accounting.order">Handling Fee</translate>
                  </div>
                  <div>
                    -{{ order.handling_fee|currency(order.currency) }}
                  </div>
                </div>
                <div class="order-row">
                  <div class="label pl-3">
                    <translate translate-context="accounting.order">Tax</translate>
                  </div>
                  <div>
                    -{{ totalItemTax|currency(order.currency) }}
                  </div>
                </div>
                <div class="order-row" v-if="hasTransactionFee">
                  <div class="label pl-3">
                    <translate translate-context="accounting.order">Transaction Fee</translate>
                  </div>
                  <div>
                    -{{ order.payment_transaction_fee|currency(order.currency) }}
                  </div>
                </div>
                <div class="order-row" v-if="totalPlatformFees > 0">
                  <div class="label pl-3">
                    <translate translate-context="accounting.order">Platform Fees</translate>
                  </div>
                  <div>
                    -{{ totalPlatformFees|currency(order.currency) }}
                  </div>
                </div>

                <!-- Net Revenue: result -->
                <div class="order-row order-row--border" v-if="netRevenue > 0">
                  <div class="label font-weight-semi-bold">
                    <translate translate-context="accounting.order">Net Revenue</translate>
                  </div>
                  <div class="font-weight-semi-bold">
                    {{ netRevenue|currency(order.currency) }}
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
export default {
  name: 'OrderHeader',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  computed: {
    statusColor() {
      const colors = {
        'paid': 'success',
        'pending': 'warning',
        'failed': 'danger',
        'refunded': 'info',
        'cancelled': 'secondary'
      }
      return colors[this.order.status?.toLowerCase()] || 'secondary'
    },
    paymentMethodIcon() {
      const methodName = this.order.payment_method?.name?.toLowerCase() || ''
      const icons = {
        'stripe': 'cipCreditCard02',
        'card': 'cipCreditCard02',
        'bank_transfer': 'cipBuilding',
        'paypal': undefined,
        'apple_pay': undefined,
        'google_pay': undefined
      }
      return icons[methodName] || 'cipCreditCard02'
    },
    itemsSubtotal() {
      const digital = parseFloat(this.order.price_digital_items_total) || 0
      const print = parseFloat(this.order.price_print_items_total) || 0
      return digital + print
    },
    hasShipping() {
      return parseFloat(this.order.price_shipping) > 0
    },
    hasHandlingFee() {
      return parseFloat(this.order.handling_fee) > 0
    },
    hasTransactionFee() {
      return parseFloat(this.order.payment_transaction_fee) > 0
    },
    totalItemTax() {
      if (!this.order.order_items) return parseFloat(this.order.amount_tax) || 0
      return this.order.order_items.reduce((sum, item) => {
        return sum + (parseFloat(item.amount_tax) || 0)
      }, 0)
    },
    totalPlatformFees() {
      if (!this.order.order_items) return 0
      return this.order.order_items.reduce((sum, item) => {
        const pica = parseFloat(item.platform_fee_pica) || 0
        const additional = parseFloat(item.platform_fee_additional) || 0
        return sum + pica + additional
      }, 0)
    },
    netRevenue() {
      if (!this.order.order_items) {
        const grandTotal = parseFloat(this.order.price_grandtotal) || 0
        const txFee = parseFloat(this.order.payment_transaction_fee) || 0
        return grandTotal - txFee - this.totalPlatformFees
      }
      return this.order.order_items.reduce((sum, item) => {
        return sum + (parseFloat(item.amount_net_photo_service) || 0)
      }, 0)
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },
    goToOrderDetail() {
      this.$router.push({
        name: 'orderDetail',
        params: {orderId: this.order.id}
      })
    }
  }
}
</script>

<style scoped>

.payment-badge {
  border: 1px solid #dee2e6;
  color: #495057;
}


.amounts-table td {
  padding: 0.5rem 0;
  vertical-align: middle;
}


.grand-total-row td {
  color: #212529;
}


.net-revenue-row td {
  color: #212529;
}
</style>
